/**
 * The two kinds of channel row.
 *
 * From docs/references/17-updates-top.png and 18-updates-channels.png — two
 * genuinely different rows, not one row with a flag:
 *
 *   FollowedRow   avatar, name, last post, then the time and an unread badge
 *                 on the right. The time turns green when there is something
 *                 unread, which is the same signal the chat list uses.
 *
 *   SuggestionRow avatar, name, follower count, then a Follow pill and a
 *                 dismiss ×. No preview and no time — you have not followed
 *                 it, so there is nothing of yours to be up to date with.
 *
 * Both live here because Updates and Explore show the same rows, and a row
 * that drifts between two screens is how a list stops looking like one list.
 */

import { Link } from 'react-router-dom'
import { formatListTime, postPreview, followerLabel } from '@zivapp/shared'
import Avatar from '../ui/Avatar.jsx'
import './ChannelRows.css'

/** The tick a verified channel carries beside its name. */
function VerifiedMark () {
  return (
    <svg className="chrow__verified" viewBox="0 0 16 16" aria-label="Verified">
      <path
        d="M8 .8l1.7 1.2 2-.3.8 1.9 1.9.8-.3 2L15.2 8l-1.1 1.6.3 2-1.9.8-.8 1.9-2-.3L8 15.2l-1.7-1.2-2 .3-.8-1.9-1.9-.8.3-2L.8 8l1.1-1.6-.3-2 1.9-.8.8-1.9 2 .3L8 .8Z"
        fill="currentColor"
      />
      <path d="m5.2 8.1 1.9 1.9 3.7-3.9" stroke="var(--ziv-bg)" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  )
}

/** A channel you follow, in the Updates list. */
export function FollowedRow ({ channel }) {
  const unread = channel.unreadCount > 0
  const when = channel.lastPost?.createdAt

  return (
    <Link className="chrow" to={`/channels/${channel.id}`}>
      <Avatar name={channel.name} url={channel.avatarUrl} size={49} />

      <span className="chrow__main">
        <span className="chrow__top">
          <span className="chrow__name">{channel.name}</span>
          {channel.isVerified && <VerifiedMark />}
        </span>
        <span className="chrow__sub">
          {channel.lastPost
            ? postPreview({ ...channel.lastPost, body: channel.lastPost.preview })
            : 'No posts yet'}
        </span>
      </span>

      <span className="chrow__end">
        {when && (
          <span className={`chrow__time${unread ? ' is-unread' : ''}`}>
            {formatListTime(when)}
          </span>
        )}
        {unread && (
          <span className="chrow__badge">
            {/* Three digits is the width the row was designed for; a channel
                you have ignored for a month must not push the name out. */}
            {channel.unreadCount > 999 ? '999+' : channel.unreadCount}
          </span>
        )}
      </span>
    </Link>
  )
}

/**
 * A channel you do not follow yet.
 *
 * `onDismiss` is optional: the × belongs on a suggestion you did not ask for,
 * not on a search result you typed the name of.
 */
export function SuggestionRow ({ channel, onFollow, onDismiss, busy = false }) {
  return (
    <div className="chrow chrow--suggestion">
      <Link className="chrow__hit" to={`/channels/${channel.id}`}>
        <Avatar name={channel.name} url={channel.avatarUrl} size={49} />
        <span className="chrow__main">
          <span className="chrow__top">
            <span className="chrow__name">{channel.name}</span>
            {channel.isVerified && <VerifiedMark />}
          </span>
          <span className="chrow__sub">{followerLabel(channel.followerCount)}</span>
        </span>
      </Link>

      <button
        type="button"
        className="chrow__follow"
        onClick={() => onFollow(channel)}
        disabled={busy}
      >
        {busy ? 'Following…' : 'Follow'}
      </button>

      {onDismiss && (
        <button
          type="button"
          className="chrow__dismiss"
          onClick={() => onDismiss(channel)}
          aria-label={`Dismiss ${channel.name}`}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  )
}
